import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useMediaQuery } from '../../lib/hooks/useMediaQuery';
import { ErrorState } from './ErrorState';

const EDS3_BREAKPOINT_XL = 1280;
const MIN_WIDTH_BREAKPOINT = `(min-width: ${EDS3_BREAKPOINT_XL}px)`;

type MenuNextGlobal = {
  render: (renderProps: unknown) => void;
  setNavigationData: (data: unknown) => void;
};

type MenuNextProps = {
  navigationData?: unknown;
};

const PROD_AU_CDN_URL = 'https://hcm.cdn.elmotalent.com.au';
const STAGING_CDN_URL = 'https://hcm.cdn.elmodev.com';
const PROD_NZ_CDN_URL = 'https://hcm.cdn.elmotalent.co.nz';
const PROD_GOV_CDN_URL = 'https://hcm-cdn.elmogov.com.au';

export const MenuNext = ({ navigationData }: MenuNextProps): React.ReactElement => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const isMenuExpanded = useMediaQuery(MIN_WIDTH_BREAKPOINT);

  useLayoutEffect(() => {
    const script = document.createElement('script');
    script.type = 'module';

    const { hostname } = window.location;

    let cdnUrl = PROD_AU_CDN_URL;

    if (hostname.endsWith('elmodev.com')) {
      cdnUrl = STAGING_CDN_URL;
    }

    if (hostname.endsWith('elmotalent.co.nz')) {
      cdnUrl = PROD_NZ_CDN_URL;
    }

    if (hostname.endsWith('elmogov.com.au')) {
      cdnUrl = PROD_GOV_CDN_URL;
    }

    script.src = `${cdnUrl}/menu-next/main.js`;
    script.crossOrigin = 'anonymous';

    script.onload = (): void => {
      // @ts-expect-error MenuNext is expected to be globally available after script load
      const GlobalMenuNext = window.MenuNext as MenuNextGlobal;

      if (GlobalMenuNext) {
        GlobalMenuNext.render({
          rootElement: document.getElementById('elmo-navigation-menu'),
          autoFetchNavigationData: false,
        });
      } else {
        setIsError(true);
      }

      setIsScriptLoaded(true);
    };

    script.addEventListener('error', (): void => setIsError(true));

    document.body.appendChild(script);

    return (): void => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect((): void => {
    if (isScriptLoaded && navigationData) {
      // @ts-expect-error MenuNext is expected to be globally available after script load
      const GlobalMenuNext = window.MenuNext as MenuNextGlobal;

      if (GlobalMenuNext) {
        GlobalMenuNext.setNavigationData(navigationData);
      } else {
        setIsError(true);
      }
    }
  }, [isScriptLoaded, navigationData]);

  return isError ? (
    <ErrorState isMenuExpanded={isMenuExpanded} />
  ) : (
    <div id="elmo-navigation-menu" />
  );
};
